// @ts-check
// Settings por tipo que a pessoa escolhe no editor de campos (doc 04, seção 2.5; doc 11, seção 3.6.5), valor
// padrão aceito por tipo (doc 04, seção 2.3) e tipos que aceitam índice simples. Puro e isomórfico: o motor valida
// antes de gravar e a SPA usa as mesmas regras para montar o formulário e para exibir e editar os valores.

export const NUMBER_VARIANTS = Object.freeze(['number', 'percentage']);
export const NUMBER_DATA_TYPES = Object.freeze(['float', 'int']);
export const CURRENCY_FORMATS = Object.freeze(['short', 'full']);
export const DATE_DISPLAY_FORMATS = Object.freeze(['USER_SETTINGS', 'RELATIVE']);
export const CLICK_ACTIONS = Object.freeze(['OPEN_LINK', 'COPY']);
export const MAX_NUMBER_DECIMALS = 10;
export const MAX_CURRENCY_DECIMALS = 5;
export const MAX_DISPLAYED_ROWS = 99;
export const DEFAULT_CURRENCY_CODE = 'BRL';
export const DEFAULT_PHONE_COUNTRY_CODE = 'BR';
// cada índice deixa toda escrita no objeto um pouco mais lenta; o limite evita que o editor vire um gargalo
export const MAX_CUSTOM_INDEXES_PER_OBJECT = 20;

// tipos de coluna simples em que um índice B-tree acelera filtro e ordenação; compostos já nascem com índice
// no sub-campo principal (indexedSubFields) e JSON não se beneficia de índice simples
export const INDEXABLE_FIELD_TYPES = Object.freeze(['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATE_TIME', 'SELECT', 'RATING', 'UUID']);

// sem valor padrão, como FIELD_METADATA_TYPES_WITHOUT_DEFAULT_VALUE do Twenty (mais os tipos mantidos pelo sistema)
export const TYPES_WITHOUT_DEFAULT_VALUE = Object.freeze(['RELATION', 'MORPH_RELATION', 'FILES', 'TS_VECTOR', 'POSITION', 'ACTOR']);

/**
 * @typedef {{ kind: 'integer', minimum: number, maximum: number } | { kind: 'enum', values: ReadonlyArray<string> }} SettingRule
 */

/** @param {number} minimum @param {number} maximum @returns {SettingRule} */
const integer = (minimum, maximum) => ({ kind: 'integer', minimum, maximum });
/** @param {ReadonlyArray<string>} values @returns {SettingRule} */
const oneOf = (values) => ({ kind: 'enum', values });

/** @type {Readonly<Record<string, Readonly<Record<string, SettingRule>>>>} */
const DISPLAY_SETTINGS_BY_TYPE = Object.freeze({
  NUMBER: { decimals: integer(0, MAX_NUMBER_DECIMALS), type: oneOf(NUMBER_VARIANTS) },
  CURRENCY: { format: oneOf(CURRENCY_FORMATS), decimals: integer(0, MAX_CURRENCY_DECIMALS) },
  DATE: { displayFormat: oneOf(DATE_DISPLAY_FORMATS) },
  DATE_TIME: { displayFormat: oneOf(DATE_DISPLAY_FORMATS) },
  TEXT: { displayedMaxRows: integer(0, MAX_DISPLAYED_ROWS) },
  EMAILS: { clickAction: oneOf(CLICK_ACTIONS) },
  LINKS: { clickAction: oneOf(CLICK_ACTIONS) },
  PHONES: { clickAction: oneOf(CLICK_ACTIONS) },
});

// só na criação: muda o tipo da coluna (INTEGER ou REAL) e a validação dos valores já gravados
const CREATE_ONLY_SETTINGS_BY_TYPE = Object.freeze({ NUMBER: { dataType: oneOf(NUMBER_DATA_TYPES) } });

const SETTING_LABELS = Object.freeze({
  decimals: 'casas decimais',
  type: 'tipo de número',
  format: 'formato',
  displayFormat: 'formato da data',
  displayedMaxRows: 'linhas exibidas',
  clickAction: 'ação ao clicar',
  dataType: 'tipo de dado',
  isIndexed: 'índice',
});

export class FieldSettingsError extends Error {
  /** @param {string} message @param {string} reason código estável para a tela */
  constructor(message, reason) {
    super(message);
    this.name = 'FieldSettingsError';
    this.reason = reason;
  }
}

/** Chaves de settings que a pessoa pode mudar num campo do tipo, na criação ou na edição. */
export function getUserSettingKeys(/** @type {string} */ type, { isCreate = false, isCustom = true } = {}) {
  const keys = Object.keys(DISPLAY_SETTINGS_BY_TYPE[type] ?? {});
  if (isCreate) keys.push(...Object.keys(/** @type {Record<string, unknown>} */ (CREATE_ONLY_SETTINGS_BY_TYPE)[type] ?? {}));
  if (isCustom && INDEXABLE_FIELD_TYPES.includes(type)) keys.push('isIndexed');
  return keys;
}

/**
 * Valida as settings enviadas pela pessoa e devolve só as chaves conhecidas; `null` numa chave remove a escolha
 * (volta ao padrão). Chaves internas (relação, sub-campos materializados) nunca chegam por aqui.
 * @param {string} type
 * @param {unknown} settings
 * @param {{ isCreate?: boolean, isCustom?: boolean, typeLabel?: string }} [options]
 * @returns {Record<string, unknown>}
 */
export function normalizeUserFieldSettings(type, settings, { isCreate = false, isCustom = true, typeLabel = type } = {}) {
  if (settings === null || settings === undefined) return {};
  if (typeof settings !== 'object' || Array.isArray(settings)) {
    throw new FieldSettingsError('As configurações do campo precisam ser um objeto.', 'INVALID_SETTINGS');
  }
  const rules = {
    ...(DISPLAY_SETTINGS_BY_TYPE[type] ?? {}),
    ...(isCreate ? /** @type {Record<string, SettingRule>} */ (/** @type {Record<string, unknown>} */ (CREATE_ONLY_SETTINGS_BY_TYPE)[type] ?? {}) : {}),
  };
  const allowedKeys = getUserSettingKeys(type, { isCreate, isCustom });
  /** @type {Record<string, unknown>} */
  const result = {};
  for (const [key, value] of Object.entries(/** @type {Record<string, unknown>} */ (settings))) {
    if (!allowedKeys.includes(key)) {
      if (key === 'dataType' && type === 'NUMBER') {
        throw new FieldSettingsError('O tipo de dado de um campo número só pode ser escolhido na criação.', 'SETTING_CREATE_ONLY');
      }
      if (key === 'isIndexed') {
        throw new FieldSettingsError(
          isCustom ? `Campos do tipo ${typeLabel} não aceitam índice simples.` : 'Índices só podem ser criados em campos personalizados.',
          'INDEX_NOT_ALLOWED',
        );
      }
      throw new FieldSettingsError(`A configuração '${key}' não existe para campos do tipo ${typeLabel}.`, 'UNKNOWN_SETTING');
    }
    if (value === null) {
      result[key] = null;
      continue;
    }
    if (key === 'isIndexed') {
      if (typeof value !== 'boolean') throw new FieldSettingsError('O índice precisa ser ligado (true) ou desligado (false).', 'INVALID_SETTING_VALUE');
      result[key] = value;
      continue;
    }
    const rule = rules[key];
    const label = /** @type {Record<string, string>} */ (SETTING_LABELS)[key] ?? key;
    if (rule.kind === 'integer') {
      if (typeof value !== 'number' || !Number.isInteger(value) || value < rule.minimum || value > rule.maximum) {
        throw new FieldSettingsError(`O valor de ${label} precisa ser um número inteiro de ${rule.minimum} a ${rule.maximum}.`, 'INVALID_SETTING_VALUE');
      }
    } else if (typeof value !== 'string' || !rule.values.includes(value)) {
      throw new FieldSettingsError(`O valor de ${label} precisa ser um destes: ${rule.values.join(', ')}.`, 'INVALID_SETTING_VALUE');
    }
    result[key] = value;
  }
  return result;
}

/**
 * Junta settings atuais e o patch validado; chaves com null saem.
 * @param {Record<string, any> | null | undefined} current @param {Record<string, unknown>} patch
 * @returns {Record<string, any> | null}
 */
export function mergeFieldSettings(current, patch) {
  const merged = { ...(current ?? {}), ...patch };
  for (const [key, value] of Object.entries(merged)) if (value === null) delete merged[key];
  return Object.keys(merged).length > 0 ? merged : null;
}

/** O campo tem índice simples pedido pela pessoa. */
export const isFieldIndexedByUser = (/** @type {{ isCustom?: boolean, type: string, settings?: Record<string, any> | null }} */ field) =>
  Boolean(field.isCustom) && INDEXABLE_FIELD_TYPES.includes(field.type) && field.settings?.isIndexed === true;

/** O tipo aceita valor padrão escolhido pela pessoa. */
export const canHaveDefaultValue = (/** @type {string} */ type) => !TYPES_WITHOUT_DEFAULT_VALUE.includes(type);

/** Moeda que um campo CURRENCY usa quando o valor não traz a moeda: a do valor padrão, senão BRL. */
export function getDefaultCurrencyCode(/** @type {{ defaultValue?: unknown, settings?: Record<string, any> | null }} */ field) {
  const fromDefault = unquoteDefault(/** @type {any} */ (field.defaultValue)?.currencyCode);
  if (typeof fromDefault === 'string' && /^[A-Z]{3}$/.test(fromDefault)) return fromDefault;
  if (typeof field.settings?.currencyCode === 'string') return field.settings.currencyCode;
  return DEFAULT_CURRENCY_CODE;
}

/** País padrão de um campo PHONES: o do valor padrão, senão Brasil. */
export function getDefaultPhoneCountryCode(/** @type {{ defaultValue?: unknown }} */ field) {
  const fromDefault = unquoteDefault(/** @type {any} */ (field.defaultValue)?.primaryPhoneCountryCode);
  return typeof fromDefault === 'string' && /^[A-Z]{2}$/.test(fromDefault) ? fromDefault : DEFAULT_PHONE_COUNTRY_CODE;
}

/** "'BRL'" vira "BRL" (convenção de default do Twenty, doc 04, 2.3). */
export function unquoteDefault(/** @type {unknown} */ value) {
  if (typeof value !== 'string') return value;
  return value.length >= 2 && value.startsWith("'") && value.endsWith("'") ? value.slice(1, -1) : value;
}

/** Texto entre aspas simples, como o Twenty grava defaults literais. */
export const quoteDefault = (/** @type {string} */ value) => `'${value}'`;
